<template>
  <div class="watchlist">

    <h1 style="position: fixed; top: -100px; color: transparent; z-index: 0; height: 0">Your Watchlist</h1>
    <div style="position: fixed; top: -100px; color: transparent; z-index: 0; height: 0">Keep an eye on the vehicles you've saved, from tracking price history to comparing your favorites.</div>

    <div v-if="loading" class="full-watchlist-screen">
      <q-spinner color="text-light" size="4.5em" />
    </div>
    <transition appear name="fade" mode="out-in">
      <div v-if="!loading && listingsLoaded == 0" class="full-watchlist-screen">
        <q-card class="bg-background-light">
          <q-card-section
            :class="{ 'q-pb-xl q-px-xl q-pt-lg': windowWidth > 1025 }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="#f1f1f1"
              :style="[windowWidth <= 1025 ? { 'width': '72px' } : { 'width': '88px' }]"
              class="q-my-md"
            >
              <path
                d="M22.1 21.5L2.4 1.7L1.1 3L6.9 8.8L2 9.2L7.5 14L5.9 21L12.1 17.3L18.3 21L18 19.8L20.9 22.7L22.1 21.5M15.8 17.7L12 15.4L8.2 17.7L9.2 13.4L5.9 10.5L8.4 10.3L15.8 17.7M11.2 8L10 6.8L12 2L14.8 8.6L22 9.2L16.9 13.6L15.8 12.5L18.2 10.5L13.8 10.1L12.1 6.1L11.2 8Z"
              />
            </svg>
            <h3 v-if="windowWidth > 1025" class="q-mb-sm">Your Watchlist is empty.</h3>
            <h4 v-else class="q-mb-sm">Your Watchlist is empty.</h4>
            <p class="text-text-very-light">
              Click the
              <q-icon name="sym_r_star" size="24px" style="margin-top: -6px" />
              icon while browsing to add vehicles to your Watchlist. Once added, check back here to track price changes and compare your favorites.
            </p>

            <q-btn
              color="secondary"
              label="Start Your Search"
              :class="{ 'q-mt-xl': windowWidth > 1025, 'q-mt-lg': windowWidth <= 1025 }"
              to="/search"
            />
          </q-card-section>
        </q-card>
      </div>
    </transition>
    <div v-show="listingsLoaded > 0">
      <div class="q-mb-lg">
        <div class="flex items-center no-wrap">
          <q-icon
            name="sym_r_star"
            size="36px"
            class="q-mr-sm"
            style="margin-top: -2px"
          />
          <h4 class="q-mb-none">Your Watchlist</h4>
        </div>
        <div class="q-mt-xs --text-md text-text-light">
          Track price changes and compare your favorites.
        </div>
      </div>
      <VehicleListingsTable
        ref="vehicleListingsTable"
        :columns="columns"
        :filters="filters"
        :scoring="scoring"
        :sorting="sorting"
        attach-price-history
        default-sort-field="deal_score"
        default-sort-direction="desc"
        searchable
        @refresh-rows="
          () => {
            handleListingsRefresh();
          }
        "
        @loaded-totals="
          e => {
            handleListingsLoaded(e);
          }
        "
      >
        <template v-slot:title>
          <div
            class="flex items-center no-wrap text-text-light"
            :class="{ 'q-mb-md': enableMobileDisplay }"
          >
            You have
            <span class="text-weight-medium text-text q-mx-xs"
              >{{ listingsLoaded }}
              {{ listingsLoaded == 1 ? "vehicle" : "vehicles" }}</span
            >
            in your Watchlist.
          </div>
        </template>
      </VehicleListingsTable>
    </div>
  </div>
</template>

<script>
import VehicleListingsTable from "@/components/VehicleListings/VehicleListingsTable.vue";
import vehicleHelpers from "@/mixins/vehicleHelpers";

export default {
  name: "Watchlist",
  mixins: [vehicleHelpers],
  components: { VehicleListingsTable },
  data() {
    return {
      loading: true,
      listingsLoaded: 0,
      filters: [],
      scoring: [],
      sorting: [],
      savedVehicleProductIds: [],
      columns: [
        {
          name: "vehicle_overview",
          label: "Vehicle",
          field: "",
          sortable: false
        },
        {
          name: "price",
          label: "Price",
          field: "price",
          format: val =>
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(val),
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b)
        },
        {
          name: "mileage",
          label: "Mileage",
          field: "mileage",
          format: val => this.formatNumber(val) + " mi",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b)
        },
        {
          name: "deal_score",
          label: "Deal Score",
          field: "deal_score",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          hideField: true
        },
        {
          name: "latest_price_change",
          label: "Latest Price Change",
          labelHelpMessage:
            "The most recent price change and the date it happened.",
          field: row =>
            row.price_history.latest
              ? row.price_history.latest.amount_change
              : "",
          hideField: true,
          sortable: true,
          sort: (a, b) => b - a
        },
        {
          name: "total_price_change",
          label: "Lifetime Price Change",
          labelHelpMessage:
            "The total price change of the vehicle since it was listed.",
          field: row =>
            row.price_history.total
              ? row.price_history.total.amount_change
              : "",
          hideField: true,
          sortable: true,
          sort: (a, b) => b - a
        },
        {
          name: "days_on_lot",
          label: "Days On Lot",
          field: "",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b)
        },
        {
          name: "view_vehicle",
          label: "",
          field: "",
          sortable: false
        }
      ]
    };
  },
  beforeUnmount() {},
  created() {
    this.initSearch();
  },
  mounted() {},
  methods: {
    initSearch() {
      this.savedVehicleProductIds = window.localStorage.getItem(
        "jfpc-saved-vehicle-product-ids"
      )
        ? JSON.parse(
            window.localStorage.getItem("jfpc-saved-vehicle-product-ids")
          )
        : [];

      if (this.savedVehicleProductIds.length == 0) {
        this.listingsLoaded = 0;
      }

      this.filters = [{ product_id: { in: this.savedVehicleProductIds } }];

      this.scoring = [];
      this.savedVehicleProductIds.forEach((savedProductId, i) => {
        let weight = 5000 - Math.abs(i - this.savedVehicleProductIds.length);
        this.scoring.push({
          [weight]: { product_id: { equals: savedProductId } }
        });
      });

      this.sorting = [{ _score: "desc" }];
    },
    handleListingsLoaded(e) {
      if (e.results > 0) this.listingsLoaded = e.results;
      this.loading = false;
    },
    handleListingsRefresh() {
      let vehicleListingTable = this.$refs.vehicleListingsTable;
      vehicleListingTable.loading = true;
      vehicleListingTable.page = 1;
      vehicleListingTable.listings = [];
      this.initSearch();
      setTimeout(() => {
        vehicleListingTable.getVehicles();
      }, 100);
    }
  }
};
</script>

<style scoped lang="scss">
.watchlist {
  padding: $space-lg;
  min-height: 100dvh;

  @include screen-sm {
    padding: $space-md;
  }

  .full-watchlist-screen {
    width: 600px;
    max-width: 100%;
    padding: 0 $space-md;
    margin: -66px auto 0;
    @include full-screen-height;
    @include display-flex(column, center, center);
    text-align: center;
  }
}
</style>
